import React, { useState, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { socketService as socket } from "../../../services/socket";
import { CustomDialog } from "../../common/components/CustomDialog";
import { useTags } from "../../common/contexts/TagsContext";
import { CategoryCard, TagCategory, Tag } from "../components/CategoryCard";
import { CameraCard } from "../components/CameraCard";
import { DirectorCard } from "../components/DirectorCard";
import { VtrTagCard } from "../components/VtrTagCard";

export const TagsEditor: React.FC = () => {
  const { t } = useTranslation();
  const { categories, tags, cameras, directors, vtrTags } = useTags();

  // New Category State
  const [isAddingCategory, setIsAddingCategory] = useState(false);
  const [newCatName, setNewCatName] = useState("");
  const [newCatColor, setNewCatColor] = useState("#ffffff");

  // Camera State
  const [newCamName, setNewCamName] = useState("");
  const [newCamOperator, setNewCamOperator] = useState("");

  // Director State
  const [newDirName, setNewDirName] = useState("");

  // Collapsible State
  const [isTagsOpen, setIsTagsOpen] = useState(true);
  const [isCamerasOpen, setIsCamerasOpen] = useState(true);
  const [isDirectorsOpen, setIsDirectorsOpen] = useState(true);
  const [isVtrTagsOpen, setIsVtrTagsOpen] = useState(true);

  // VTR Tag State
  const [newVtrTagLabel, setNewVtrTagLabel] = useState("");
  const [newVtrTagColor, setNewVtrTagColor] = useState("#3b82f6");

  // Local color overrides (to prevent re-render while picking)
  const [localColors, setLocalColors] = useState<{ [catId: string]: string }>({});
  const colorDebounceRef = useRef<{ [catId: string]: ReturnType<typeof setTimeout> }>({});

  useEffect(() => {
    socket.emit("register-editor");
  }, []);

  const handleAddCamera = () => {
    if (!newCamName.trim()) return;
    const id = `CAM_${Date.now()}`;
    socket.emit("add-camera", { id, name: newCamName.toUpperCase(), operator: newCamOperator });
    setNewCamName("");
    setNewCamOperator("");
  };

  const handleDeleteCamera = (id: string) => {
    setDialog({
      isOpen: true,
      title: t("controller.tags.deleteCamera"),
      message: t("controller.tags.deleteCameraConfirm"),
      type: "confirm",
      onConfirm: () => {
        socket.emit("delete-camera", id);
        setDialog((prev) => ({ ...prev, isOpen: false }));
      },
    });
  };

  const handleUpdateCamera = (id: string, name: string, operator: string) => {
    socket.emit("update-camera", { id, name, operator });
  };

  const handleAddDirector = () => {
    if (!newDirName.trim()) return;
    const id = `DIR_${Date.now()}`;
    socket.emit("add-director", { id, name: newDirName.toUpperCase() });
    setNewDirName("");
  };

  const handleDeleteDirector = (id: string) => {
    setDialog({
      isOpen: true,
      title: t("controller.tags.deleteDirector"),
      message: t("controller.tags.deleteDirectorConfirm"),
      type: "confirm",
      onConfirm: () => {
        socket.emit("delete-director", id);
        setDialog((prev) => ({ ...prev, isOpen: false }));
      },
    });
  };

  const handleUpdateDirector = (id: string, name: string) => {
    socket.emit("update-director", { id, name });
  };

  const handleAddVtrTag = () => {
    if (!newVtrTagLabel.trim()) return;
    const id = `VTR_TAG_${Date.now()}`;
    socket.emit("add-vtr-tag", { id, label: newVtrTagLabel.toUpperCase(), color: newVtrTagColor });
    setNewVtrTagLabel("");
    setNewVtrTagColor("#3b82f6");
  };

  const handleDeleteVtrTag = (id: string) => {
    setDialog({
      isOpen: true,
      title: t("controller.tags.deleteVtrTag", "Delete VTR Tag"),
      message: t(
        "controller.tags.deleteVtrTagConfirm",
        "Are you sure you want to delete this VTR tag?",
      ),
      type: "confirm",
      onConfirm: () => {
        socket.emit("delete-vtr-tag", id);
        setDialog((prev) => ({ ...prev, isOpen: false }));
      },
    });
  };

  const handleUpdateVtrTag = (id: string, label: string, color: string) => {
    socket.emit("update-vtr-tag", { id, label, color });
  };

  // Dialog State
  const [dialog, setDialog] = useState<{
    isOpen: boolean;
    title: string;
    message: string;
    type: "alert" | "prompt" | "confirm";
    onConfirm: () => void;
  }>({
    isOpen: false,
    title: "",
    message: "",
    type: "alert",
    onConfirm: () => {},
  });

  const handleAddCategory = () => {
    if (!newCatName.trim()) return;
    socket.emit("add-tag-category", { id: newCatName.toUpperCase(), color: newCatColor });
    setNewCatName("");
    setNewCatColor("#ffffff");
    setIsAddingCategory(false);
  };

  const handleDeleteCategory = (id: string) => {
    setDialog({
      isOpen: true,
      title: t("controller.tags.deleteCategory"),
      message: t("controller.tags.deleteCategoryConfirm", { id }),
      type: "confirm",
      onConfirm: () => {
        socket.emit("delete-tag-category", id);
        setDialog((prev) => ({ ...prev, isOpen: false }));
      },
    });
  };

  const handleUpdateCategoryColor = (id: string, color: string) => {
    // Update locally immediately (no server round-trip while picking)
    setLocalColors((prev) => ({ ...prev, [id]: color }));
    // No auto-sync to avoid re-renders closing the picker
  };

  // Flush pending color on picker close
  const handleColorPickerClose = (id: string) => {
    const pendingColor = localColors[id];
    if (pendingColor) {
      socket.emit("update-tag-category", { id, color: pendingColor });
      // Optional: clear local override to let props take over
      // but wait a bit to ensure server response arrives
      setTimeout(() => {
        setLocalColors((prev) => {
          const next = { ...prev };
          delete next[id];
          return next;
        });
      }, 1000);
    }
  };

  const handleMoveCategory = (id: string, direction: "left" | "right") => {
    socket.emit("move-tag-category", { id, direction });
  };

  const handleAddTag = (categoryId: string, label: string) => {
    const id = `TAG_${Date.now()}_${crypto.randomUUID().substring(0, 8)}`;
    socket.emit("save-tag", { id, label, categoryId });
  };

  const handleDeleteTag = (id: string) => {
    setDialog({
      isOpen: true,
      title: t("controller.tags.deleteTag"),
      message: t("controller.tags.deleteTagConfirm"),
      type: "confirm",
      onConfirm: () => {
        socket.emit("delete-tag", id);
        setDialog((prev) => ({ ...prev, isOpen: false }));
      },
    });
  };

  const handleUpdateTag = (tag: Tag, newLabel: string) => {
    socket.emit("save-tag", { ...tag, label: newLabel });
  };

  return (
    <div data-testid="tags-editor" style={{ padding: "20px", height: "100%", overflowY: "auto" }}>
      <div
        data-testid="tags-section-header"
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: "20px",
        }}
      >
        <h2
          data-testid="tags-section-header"
          onClick={() => setIsTagsOpen(!isTagsOpen)}
          style={{
            margin: 0,
            letterSpacing: "2px",
            color: "var(--color-text-primary)",
            cursor: "pointer",
            display: "flex",
            alignItems: "center",
            gap: "10px",
          }}
        >
          <i
            data-testid="tags-section-toggle-icon"
            className={`bx ${isTagsOpen ? "bx-caret-down" : "bx-caret-right"}`}
            style={{ fontSize: "0.8em", color: "var(--color-role-controller)" }}
          ></i>{" "}
          {t("controller.tags.title")}
        </h2>
        <div data-testid="tags-section-actions">
          <button
            data-testid="apply-config-button"
            onClick={() => {
              setDialog({
                isOpen: true,
                title: t("controller.tags.confirmUpdate"),
                message: t("controller.tags.confirmUpdateMsg"),
                type: "confirm",
                onConfirm: () => {
                  socket.emit("force-refresh-tags");
                  setDialog({
                    isOpen: true,
                    title: t("controller.tags.configApplied"),
                    message: t("controller.tags.configAppliedMsg"),
                    type: "alert",
                    onConfirm: () => setDialog((prev) => ({ ...prev, isOpen: false })),
                  });
                },
              });
            }}
            style={{
              background: "var(--color-accent-primary)",
              border: "none",
              padding: "10px 20px",
              borderRadius: "6px",
              fontWeight: "bold",
              cursor: "pointer",
              marginRight: "10px",
              color: "rgba(255, 255, 255, 0.8)",
            }}
          >
            {t("controller.tags.applyConfig")}
          </button>
          <button
            data-testid="new-category-button"
            onClick={() => {
              if (categories.length >= 5) {
                setDialog({
                  isOpen: true,
                  title: t("controller.tags.limitReached"),
                  message: t("controller.tags.limitReachedMsg"),
                  type: "alert",
                  onConfirm: () => setDialog((prev) => ({ ...prev, isOpen: false })),
                });
                return;
              }
              setIsAddingCategory(true);
            }}
            style={{
              background: "var(--color-accent-success)",
              border: "none",
              padding: "10px 20px",
              borderRadius: "6px",
              fontWeight: "bold",
              cursor: "pointer",
              color: "rgba(255, 255, 255, 0.8)",
              opacity: categories.length >= 5 ? 0.5 : 1,
            }}
          >
            {t("controller.tags.newCategory")}
          </button>
        </div>
      </div>

      {isTagsOpen && (
        <>
          {isAddingCategory && (
            <div
              data-testid="new-category-form"
              style={{
                padding: "20px",
                background: "var(--color-panel-bg)",
                borderRadius: "var(--border-radius-lg)",
                marginBottom: "20px",
                border: "1px solid var(--color-panel-border)",
                display: "flex",
                gap: "15px",
                alignItems: "center",
                boxShadow: "var(--shadow-card)",
              }}
            >
              <input
                data-testid="new-category-name-input"
                value={newCatName}
                onChange={(e) => setNewCatName(e.target.value)}
                placeholder={t("controller.tags.categoryNamePlaceholder")}
                style={{
                  padding: "10px",
                  background: "var(--color-input-bg)",
                  border: "1px solid var(--color-panel-border)",
                  color: "var(--color-text-primary)",
                  fontWeight: "bold",
                  flex: 1,
                  borderRadius: "var(--border-radius-sm)",
                }}
              />
              <input
                data-testid="new-category-color-input"
                type="color"
                value={newCatColor}
                onChange={(e) => setNewCatColor(e.target.value)}
                style={{
                  width: "50px",
                  height: "40px",
                  border: "none",
                  background: "transparent",
                  cursor: "pointer",
                }}
              />
              <button
                data-testid="new-category-save-button"
                onClick={handleAddCategory}
                style={{
                  background: "var(--color-accent-success)",
                  border: "none",
                  padding: "10px 20px",
                  borderRadius: "4px",
                  fontWeight: "bold",
                  color: "#000",
                }}
              >
                {t("controller.tags.save")}
              </button>
              <button
                data-testid="new-category-cancel-button"
                onClick={() => setIsAddingCategory(false)}
                style={{
                  background: "var(--color-accent-danger)",
                  color: "white",
                  border: "none",
                  padding: "10px 20px",
                  borderRadius: "4px",
                  fontWeight: "bold",
                }}
              >
                {t("controller.tags.cancel")}
              </button>
            </div>
          )}

          <div
            data-testid="categories-grid"
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fill, minmax(300px, 1fr))",
              gap: "20px",
              marginBottom: "40px",
            }}
          >
            {[...categories]
              .sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
              .map((cat) => {
                const displayColor = localColors[cat.id] || cat.color;
                return (
                  <CategoryCard
                    key={cat.id}
                    category={cat}
                    tags={tags.filter((tag) => tag.categoryId === cat.id)}
                    displayColor={displayColor}
                    onMove={(direction) => handleMoveCategory(cat.id, direction)}
                    onColorChange={(color) => handleUpdateCategoryColor(cat.id, color)}
                    onColorCommit={() => handleColorPickerClose(cat.id)}
                    onDelete={() => handleDeleteCategory(cat.id)}
                    onAddTag={(label) => handleAddTag(cat.id, label)}
                    onDeleteTag={handleDeleteTag}
                    onUpdateTag={handleUpdateTag}
                  />
                );
              })}
          </div>
        </>
      )}

      {/* CAMERAS CONFIGURATION */}
      <h2
        data-testid="cameras-section-header"
        onClick={() => setIsCamerasOpen(!isCamerasOpen)}
        style={{
          margin: "40px 0 20px 0",
          letterSpacing: "2px",
          color: "var(--color-text-primary)",
          borderTop: "1px solid var(--color-panel-border)",
          paddingTop: "20px",
          cursor: "pointer",
          display: "flex",
          alignItems: "center",
          gap: "10px",
        }}
      >
        <i
          data-testid="cameras-section-toggle-icon"
          className={`bx ${isCamerasOpen ? "bx-caret-down" : "bx-caret-right"}`}
          style={{ fontSize: "0.8em", color: "var(--color-role-controller)" }}
        ></i>{" "}
        {t("controller.tags.camerasConfig")}
      </h2>

      {isCamerasOpen && (
        <>
          <div
            data-testid="add-camera-form"
            style={{ marginBottom: "20px", display: "flex", gap: "10px" }}
          >
            <input
              data-testid="new-camera-name-input"
              value={newCamName}
              onChange={(e) => setNewCamName(e.target.value)}
              placeholder={t("controller.tags.cameraNamePlaceholder")}
              style={{
                padding: "10px",
                background: "var(--color-input-bg)",
                border: "1px solid var(--color-panel-border)",
                color: "var(--color-text-primary)",
                fontWeight: "bold",
                borderRadius: "var(--border-radius-sm)",
              }}
            />
            <input
              data-testid="new-camera-operator-input"
              value={newCamOperator}
              onChange={(e) => setNewCamOperator(e.target.value)}
              placeholder={t("controller.tags.operatorPlaceholder")}
              style={{
                padding: "10px",
                background: "var(--color-input-bg)",
                border: "1px solid var(--color-panel-border)",
                color: "var(--color-text-primary)",
                fontWeight: "bold",
                flex: 1,
                borderRadius: "var(--border-radius-sm)",
              }}
            />
            <button
              data-testid="add-camera-button"
              onClick={handleAddCamera}
              style={{
                background: "var(--color-accent-success)",
                border: "none",
                padding: "10px 20px",
                borderRadius: "4px",
                fontWeight: "bold",
                cursor: "pointer",
                color: "#000",
              }}
            >
              {t("controller.tags.addCamera")}
            </button>
          </div>

          <div
            data-testid="cameras-grid"
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fill, minmax(200px, 1fr))",
              gap: "10px",
            }}
          >
            {cameras.map((cam) => (
              <CameraCard
                key={cam.id}
                camera={cam}
                onDelete={() => handleDeleteCamera(cam.id)}
                onUpdate={(name, operator) => handleUpdateCamera(cam.id, name, operator)}
              />
            ))}
          </div>
        </>
      )}

      {/* DIRECTORS CONFIGURATION */}
      <h2
        data-testid="directors-section-header"
        onClick={() => setIsDirectorsOpen(!isDirectorsOpen)}
        style={{
          margin: "40px 0 20px 0",
          letterSpacing: "2px",
          color: "var(--color-text-primary)",
          borderTop: "1px solid var(--color-panel-border)",
          paddingTop: "20px",
          cursor: "pointer",
          display: "flex",
          alignItems: "center",
          gap: "10px",
        }}
      >
        <i
          data-testid="directors-section-toggle-icon"
          className={`bx ${isDirectorsOpen ? "bx-caret-down" : "bx-caret-right"}`}
          style={{ fontSize: "0.8em", color: "var(--color-role-executive)" }}
        ></i>{" "}
        {t("controller.tags.directorsConfig")}
      </h2>

      {isDirectorsOpen && (
        <>
          <div
            data-testid="add-director-form"
            style={{ marginBottom: "20px", display: "flex", gap: "10px" }}
          >
            <input
              data-testid="new-director-name-input"
              value={newDirName}
              onChange={(e) => setNewDirName(e.target.value)}
              placeholder={t("controller.tags.directorNamePlaceholder")}
              style={{
                padding: "10px",
                background: "var(--color-input-bg)",
                border: "1px solid var(--color-panel-border)",
                color: "var(--color-text-primary)",
                fontWeight: "bold",
                flex: 1,
                borderRadius: "var(--border-radius-sm)",
              }}
            />
            <button
              data-testid="add-director-button"
              onClick={handleAddDirector}
              style={{
                background: "var(--color-accent-success)",
                border: "none",
                padding: "10px 20px",
                borderRadius: "4px",
                fontWeight: "bold",
                cursor: "pointer",
                color: "#000",
              }}
            >
              {t("controller.tags.addDirector")}
            </button>
          </div>

          <div
            data-testid="directors-grid"
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fill, minmax(200px, 1fr))",
              gap: "10px",
            }}
          >
            {directors.map((dir) => (
              <DirectorCard
                key={dir.id}
                director={dir}
                onDelete={() => handleDeleteDirector(dir.id)}
                onUpdate={(name) => handleUpdateDirector(dir.id, name)}
              />
            ))}
          </div>
        </>
      )}

      {/* VTR TAGS CONFIGURATION */}
      <h2
        data-testid="vtr-tags-section-header"
        onClick={() => setIsVtrTagsOpen(!isVtrTagsOpen)}
        style={{
          margin: "40px 0 20px 0",
          letterSpacing: "2px",
          color: "var(--color-text-primary)",
          borderTop: "1px solid var(--color-panel-border)",
          paddingTop: "20px",
          cursor: "pointer",
          display: "flex",
          alignItems: "center",
          gap: "10px",
        }}
      >
        <i
          data-testid="vtr-tags-section-toggle-icon"
          className={`bx ${isVtrTagsOpen ? "bx-caret-down" : "bx-caret-right"}`}
          style={{ fontSize: "0.8em", color: "var(--color-role-sp)" }}
        ></i>{" "}
        {t("controller.tags.vtrTagsConfig", "VTR TAGS")}
      </h2>

      {isVtrTagsOpen && (
        <>
          <div
            data-testid="add-vtr-tag-form"
            style={{ marginBottom: "20px", display: "flex", gap: "10px", alignItems: "center" }}
          >
            <input
              type="color"
              value={newVtrTagColor}
              onChange={(e) => setNewVtrTagColor(e.target.value)}
              style={{
                width: "36px",
                height: "36px",
                border: "none",
                background: "transparent",
                cursor: "pointer",
                padding: 0,
                flexShrink: 0,
              }}
              title="Tag color"
            />
            <input
              data-testid="new-vtr-tag-label-input"
              value={newVtrTagLabel}
              onChange={(e) => setNewVtrTagLabel(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && handleAddVtrTag()}
              placeholder={t("controller.tags.vtrTagNamePlaceholder", "Tag name")}
              style={{
                padding: "10px",
                background: "var(--color-input-bg)",
                border: "1px solid var(--color-panel-border)",
                color: "var(--color-text-primary)",
                fontWeight: "bold",
                flex: 1,
                borderRadius: "var(--border-radius-sm)",
                textTransform: "uppercase",
              }}
            />
            <button
              data-testid="add-vtr-tag-button"
              onClick={handleAddVtrTag}
              style={{
                background: "var(--color-accent-success)",
                border: "none",
                padding: "10px 20px",
                borderRadius: "4px",
                fontWeight: "bold",
                cursor: "pointer",
                color: "#000",
              }}
            >
              {t("controller.tags.addVtrTag", "Add Tag")}
            </button>
          </div>

          <div
            data-testid="vtr-tags-grid"
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fill, minmax(200px, 1fr))",
              gap: "10px",
            }}
          >
            {vtrTags.map((vt) => (
              <VtrTagCard
                key={vt.id}
                tag={vt}
                onDelete={() => handleDeleteVtrTag(vt.id)}
                onUpdate={(label, color) => handleUpdateVtrTag(vt.id, label, color)}
              />
            ))}
          </div>
        </>
      )}

      <CustomDialog
        isOpen={dialog.isOpen}
        title={dialog.title}
        message={dialog.message}
        type={dialog.type}
        onConfirm={dialog.onConfirm}
        onCancel={() => setDialog((prev) => ({ ...prev, isOpen: false }))}
      />
    </div>
  );
};
